import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { Show } from '@refinedev/antd'
import {
  Card,
  Row,
  Col,
  Avatar,
  Typography,
  Tag,
  Space,
  Button,
  Descriptions,
  Spin,
  Result,
  Divider,
  Input,
  message,
} from 'antd'
import {
  ArrowLeftOutlined,
  UserOutlined,
  PhoneOutlined,
  MailOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  VideoCameraOutlined,
  EditOutlined,
  SaveOutlined,
  CloseOutlined,
} from '@ant-design/icons'

const { Title, Text } = Typography

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3520/api'

const { TextArea } = Input

const getAuthHeaders = (): HeadersInit => {
  const token = localStorage.getItem('admin_access_token')
  return {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
  }
}

// API function to update doctor notes
async function updateDoctorNotes(appointmentId: string, notes: string): Promise<boolean> {
  try {
    const response = await fetch(`${API_URL}/admin/consultations/my/${appointmentId}/notes`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify({ notes }),
    })
    return response.ok
  } catch (err) {
    console.error('Failed to update notes:', err)
    return false
  }
}

interface AppointmentDetail {
  id: string
  patient: {
    id: string
    name: string
    email: string
    phone: string | null
    image: string | null
    dateOfBirth: string | null
    gender: string | null
  }
  type: string
  typeLabel: string
  scheduledAt: string
  duration: number
  status: string
  statusLabel: string
  roomUrl: string | null
  patientNotes: string | null
  doctorNotes: string | null
  createdAt: string
}

const statusColors: Record<string, string> = {
  SCHEDULED: 'default',
  CONFIRMED: 'processing',
  IN_PROGRESS: 'warning',
  COMPLETED: 'success',
  CANCELLED: 'error',
  NO_SHOW: 'volcano',
}

export default function MyConsultationShowPage() {
  const { id } = useParams<{ id: string }>()
  const [data, setData] = useState<AppointmentDetail | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [canJoin, setCanJoin] = useState(false)
  const [timeUntilCall, setTimeUntilCall] = useState<string | null>(null)

  // Notes form state
  const [isEditingNotes, setIsEditingNotes] = useState(false)
  const [notesValue, setNotesValue] = useState('')
  const [isSavingNotes, setIsSavingNotes] = useState(false)

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await fetch(`${API_URL}/admin/consultations/my/${id}`, {
          headers: getAuthHeaders(),
        })
        if (!res.ok) throw new Error('Failed to fetch')
        const json = await res.json()
        setData(json)
      } catch (error) {
        console.error('Error fetching appointment:', error)
      } finally {
        setIsLoading(false)
      }
    }
    fetchData()
  }, [id])

  useEffect(() => {
    if (!data) return

    const checkCanJoin = () => {
      const appointmentDate = new Date(data.scheduledAt)
      const now = new Date()
      const diffMs = appointmentDate.getTime() - now.getTime()
      const diffMins = Math.floor(diffMs / 60000)

      // Doctors can join 10 minutes before until 60 minutes after
      if (diffMins <= 10 && diffMins >= -60 && data.status !== 'COMPLETED' && data.status !== 'CANCELLED') {
        setCanJoin(true)
        setTimeUntilCall(null)
      } else if (diffMins > 10) {
        setCanJoin(false)
        if (diffMins < 60) {
          setTimeUntilCall(`${diffMins} minuta`)
        } else if (diffMins < 1440) {
          setTimeUntilCall(`${Math.floor(diffMins / 60)} sati`)
        } else {
          setTimeUntilCall(`${Math.floor(diffMins / 1440)} dana`)
        }
      } else {
        setCanJoin(false)
        setTimeUntilCall(null)
      }
    }

    checkCanJoin()
    const interval = setInterval(checkCanJoin, 60000)
    return () => clearInterval(interval)
  }, [data])

  // Initialize notes value when data is loaded
  useEffect(() => {
    if (data?.doctorNotes) {
      setNotesValue(data.doctorNotes)
    }
  }, [data?.doctorNotes])

  // Handle saving notes
  const handleSaveNotes = async () => {
    if (!id) return

    setIsSavingNotes(true)
    const success = await updateDoctorNotes(id, notesValue)

    if (success) {
      message.success('Beleske su sacuvane')
      setIsEditingNotes(false)
      // Update local data
      setData(prev => prev ? { ...prev, doctorNotes: notesValue } : null)
    } else {
      message.error('Greska pri cuvanju beleski')
    }
    setIsSavingNotes(false)
  }

  // Handle cancel editing
  const handleCancelEdit = () => {
    setNotesValue(data?.doctorNotes || '')
    setIsEditingNotes(false)
  }

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr)
    return date.toLocaleDateString('sr-Latn-RS', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    })
  }

  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr)
    return date.toLocaleTimeString('sr-Latn-RS', {
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <Spin size="large" tip="Ucitavanje..." />
      </div>
    )
  }

  if (!data) {
    return (
      <Result
        status="error"
        title="Greska"
        subTitle="Nije moguce ucitati konsultaciju"
        extra={
          <Link to="/my-consultations">
            <Button type="primary">Nazad na listu</Button>
          </Link>
        }
      />
    )
  }

  return (
    <Show
      title={data.typeLabel}
      headerButtons={
        <Tag color={statusColors[data.status]}>{data.statusLabel}</Tag>
      }
      goBack={
        <Link to="/my-consultations">
          <Button icon={<ArrowLeftOutlined />} type="text">
            Nazad
          </Button>
        </Link>
      }
    >
      {/* Join Call Card */}
      {(canJoin || timeUntilCall) && data.status !== 'COMPLETED' && data.status !== 'CANCELLED' && (
        <Card
          style={{
            marginBottom: 24,
            background: canJoin ? 'linear-gradient(135deg, #1e3a5f 0%, #2d4a6f 100%)' : '#f5f5f5',
            borderRadius: 12,
          }}
        >
          {canJoin ? (
            <Row align="middle" justify="space-between">
              <Col>
                <Space>
                  <div style={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    backgroundColor: '#f97316',
                    animation: 'pulse 2s infinite',
                  }} />
                  <Text style={{ color: 'white', fontWeight: 500 }}>
                    Konsultacija je spremna. Mozete se pridruziti sada.
                  </Text>
                </Space>
              </Col>
              <Col>
                <Link to={`/my-consultations/${data.id}/call`}>
                  <Button
                    type="primary"
                    size="large"
                    icon={<VideoCameraOutlined />}
                    style={{
                      background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
                      border: 'none',
                    }}
                  >
                    Zapocni konsultaciju
                  </Button>
                </Link>
              </Col>
            </Row>
          ) : (
            <Space>
              <ClockCircleOutlined />
              <Text>
                Konsultacija pocinje za <strong>{timeUntilCall}</strong>
              </Text>
            </Space>
          )}
        </Card>
      )}

      <Row gutter={24}>
        {/* Patient Info */}
        <Col xs={24} lg={8}>
          <Card title="Pacijent" style={{ marginBottom: 24 }}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <div style={{ textAlign: 'center', marginBottom: 16 }}>
                <Avatar
                  size={80}
                  src={data.patient.image}
                  icon={!data.patient.image && <UserOutlined />}
                  style={{ backgroundColor: '#1e3a5f' }}
                />
                <Title level={4} style={{ marginTop: 12, marginBottom: 0 }}>
                  {data.patient.name}
                </Title>
              </div>

              <Divider style={{ margin: '12px 0' }} />

              <Descriptions column={1} size="small">
                <Descriptions.Item label={<MailOutlined />}>
                  <a href={`mailto:${data.patient.email}`}>{data.patient.email}</a>
                </Descriptions.Item>
                {data.patient.phone && (
                  <Descriptions.Item label={<PhoneOutlined />}>
                    <a href={`tel:${data.patient.phone}`}>{data.patient.phone}</a>
                  </Descriptions.Item>
                )}
                {data.patient.dateOfBirth && (
                  <Descriptions.Item label="Datum rodjenja">
                    {new Date(data.patient.dateOfBirth).toLocaleDateString('sr-Latn-RS')}
                  </Descriptions.Item>
                )}
                {data.patient.gender && (
                  <Descriptions.Item label="Pol">
                    {data.patient.gender === 'male' ? 'Muski' : 'Zenski'}
                  </Descriptions.Item>
                )}
              </Descriptions>

              <Divider style={{ margin: '12px 0' }} />

              <Link to={`/patients/${data.patient.id}`}>
                <Button block>Pogledaj profil</Button>
              </Link>
            </Space>
          </Card>
        </Col>

        {/* Appointment Details */}
        <Col xs={24} lg={16}>
          <Card title="Detalji konsultacije" style={{ marginBottom: 24 }}>
            <Descriptions column={{ xs: 1, sm: 2 }} bordered>
              <Descriptions.Item label="Tip konsultacije">
                {data.typeLabel}
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color={statusColors[data.status]}>{data.statusLabel}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Datum">
                <Space>
                  <CalendarOutlined />
                  {formatDate(data.scheduledAt)}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="Vreme">
                <Space>
                  <ClockCircleOutlined />
                  {formatTime(data.scheduledAt)} ({data.duration} min)
                </Space>
              </Descriptions.Item>
            </Descriptions>
          </Card>

          {/* Notes */}
          {data.patientNotes && (
            <Card title="Napomene od pacijenta" style={{ marginBottom: 24 }}>
              <Text>{data.patientNotes}</Text>
            </Card>
          )}

          {/* Doctor Notes - Always show for completed or in-progress appointments */}
          <Card
            title="Vase beleske"
            style={{ marginBottom: 24 }}
            extra={
              !isEditingNotes ? (
                <Button
                  icon={<EditOutlined />}
                  onClick={() => setIsEditingNotes(true)}
                >
                  {data.doctorNotes ? 'Izmeni' : 'Dodaj beleske'}
                </Button>
              ) : null
            }
          >
            {isEditingNotes ? (
              <Space direction="vertical" style={{ width: '100%' }}>
                <TextArea
                  value={notesValue}
                  onChange={(e) => setNotesValue(e.target.value)}
                  placeholder="Unesite beleske o konsultaciji..."
                  rows={6}
                  style={{ marginBottom: 16 }}
                />
                <Space>
                  <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    onClick={handleSaveNotes}
                    loading={isSavingNotes}
                  >
                    Sacuvaj
                  </Button>
                  <Button
                    icon={<CloseOutlined />}
                    onClick={handleCancelEdit}
                    disabled={isSavingNotes}
                  >
                    Otkazi
                  </Button>
                </Space>
              </Space>
            ) : data.doctorNotes ? (
              <Text style={{ whiteSpace: 'pre-wrap' }}>{data.doctorNotes}</Text>
            ) : (
              <Text type="secondary">Nema unetih beleski</Text>
            )}
          </Card>

          {/* Actions */}
          <Card>
            <Space>
              {data.patient.phone && (
                <Button icon={<PhoneOutlined />} href={`tel:${data.patient.phone}`}>
                  Pozovi pacijenta
                </Button>
              )}
              {canJoin && (
                // No data.roomUrl gate: the room is JIT-created by the call
                // page's join-token request (roomUrl is null for seeded/legacy
                // bookings). Mirrors the primary CTA banner above.
                <Link to={`/my-consultations/${data.id}/call`}>
                  <Button type="primary" icon={<VideoCameraOutlined />}>
                    Zapocni konsultaciju
                  </Button>
                </Link>
              )}
            </Space>
          </Card>
        </Col>
      </Row>
    </Show>
  )
}
